import { assertSchemaInput } from '@autone/utils';
import * as z from 'zod';

import {
  type GetAssortmentProductLocationSkusResponse,
  type GetAssortmentSkuLocationsResponse,
} from '../types';

type AssortmentSkuLocationResponse =
  | GetAssortmentSkuLocationsResponse
  | GetAssortmentProductLocationSkusResponse;

const pageInfoSchema = assertSchemaInput<
  AssortmentSkuLocationResponse['page_info']
>()(z.object({ total_count: z.number(), next_cursor: z.string().nullable() }));

const itemLocationSchema = assertSchemaInput<
  AssortmentSkuLocationResponse['items'][number]['location']
>()(
  z.object({
    id: z.string(),
    desc: z.string(),
    country_id: z.string().nullable(),
    country_desc: z.string().nullable(),
    region_id: z.string().nullable(),
    region_desc: z.string().nullable(),
  }),
);

const skuProductSchema = assertSchemaInput<
  AssortmentSkuLocationResponse['items'][number]['sku']['product']
>()(
  z.object({
    id: z.string(),
    department_id: z.string().nullable(),
    department_desc: z.string().nullable(),
    sub_department_id: z.string().nullable(),
    sub_department_desc: z.string().nullable(),
    style_id: z.string().nullable(),
    style_desc: z.string().nullable(),
    color_id: z.string().nullable(),
    color_desc: z.string().nullable(),
    material_id: z.string().nullable(),
    material_desc: z.string().nullable(),
    gender_id: z.string().nullable(),
    gender_desc: z.string().nullable(),
    brand_id: z.string().nullable(),
    brand_desc: z.string().nullable(),
    season_id: z.string().nullable(),
    season_desc: z.string().nullable(),
    event_id: z.string().nullable(),
    event_desc: z.string().nullable(),
    collection_type: z.string().nullable(),
    image_url: z.string().nullable(),
  }),
);

const itemSkuSchema = assertSchemaInput<
  AssortmentSkuLocationResponse['items'][number]['sku']
>()(
  z.object({
    id: z.string(),
    desc: z.string().optional(),
    size_id: z.string().nullable(),
    product: skuProductSchema,
  }),
);

const itemSystemAssortmentRecommendation = assertSchemaInput<
  AssortmentSkuLocationResponse['items'][number]['system_assortment_recommendation']
>()(z.union([z.literal('assort'), z.literal('unassort'), z.literal('hold')]));

export const assortmentSkuLocationItemSchema = assertSchemaInput<
  AssortmentSkuLocationResponse['items'][number]
>()(
  z
    .object({
      location: itemLocationSchema,
      sku: itemSkuSchema,
      sku_id: z.string().optional(),
      size_id: z.string().nullable().optional(),
      location_cluster: z.string().nullable(),
      is_assorted: z.boolean(),
      has_user_override: z.boolean().optional(),
      assorted_start_date: z.string().nullable(),
      assorted_end_date: z.string().nullable(),
      first_user_scheduled_assorted_start_date: z
        .string()
        .nullable()
        .optional(),
      initial_allocation: z.number().nullable(),
      first_sale_date: z.string().nullable(),
      latest_sale_date: z.string().nullable(),
      lifetime_sales_units: z.number(),
      lifetime_sales_value: z.number(),
      inventory_units: z.number(),
      product_groups: z.array(z.string()),
      is_managed_by_user: z.boolean(),
      system_assortment_recommendation: itemSystemAssortmentRecommendation,
      parent_location_inventory_units: z.number().nullable().optional(),
      pending_from_production_units: z.number().nullable(),
      parent_location_pending_from_production_units: z
        .number()
        .nullable()
        .optional(),
    })
    .transform(({ initial_allocation, is_assorted, ...rest }) => ({
      ...rest,
      is_assorted: {
        value: is_assorted,
        awaiting_requests: 0,
        is_disabled: false,
      },
      initial_allocation: {
        value: initial_allocation,
        awaiting_requests: 0,
        is_disabled: false,
      },
    })),
);

export const getAssortmentSkuLocationsSchema =
  assertSchemaInput<AssortmentSkuLocationResponse>()(
    z.object({
      page_info: pageInfoSchema,
      items: z.array(assortmentSkuLocationItemSchema),
    }),
  );

export type TransformedAssortmentSkuLocationsResponse = z.infer<
  typeof getAssortmentSkuLocationsSchema
>;

export type TransformedAssortmentSkuLocationsItem =
  TransformedAssortmentSkuLocationsResponse['items'][number];
